import { MouseEvent, useCallback, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  RiCheckLine,
  RiShareBoxFill,
  RiFileDownloadLine,
  RiLinkM,
  RiRefreshLine,
} from "@remixicon/react"
import { Menu, MenuItem } from "@mui/material"
import { startOfDay, addDays, parseISO } from "date-fns"

import useRouter from "@/hooks/router/useRoute"
import { readFilteredActivities, type FilteredActivityItem } from "@/app/api/activity"
import { createGlobalStore } from "@/stores/global"
import { createAccountStore } from "@/stores/account"
import { REPORT_STATUS_OBJECT } from "@/constants/report"
import IconButton from "@/components/button/IconButton"
import Selector from "@/components/form/selector/Selector"
import Checkbox from "@/components/form/checkbox/Checkbox"
import Chip from "@/components/chip/Chip"
import Tooltip from "@/components/tooltip/Tooltip"
import { resolvePeriodDates, presetToSearchParams, generateReportUrl } from "@/utils/filterPreset"
import { useCopyReportLink } from "@/hooks/useCopyReportLink"
import type { FilterPreset } from "@/types/filterPreset"

interface QuickReportPresetRowProps {
  preset: FilterPreset
  onPdfExport: (preset: FilterPreset) => void
  isPdfBusy: boolean
}

export default function QuickReportPresetRow({
  preset,
  onPdfExport,
  isPdfBusy,
}: QuickReportPresetRowProps) {
  const { t } = useTranslation()
  const router = useRouter()
  const setSnackBar = createGlobalStore((s) => s.setSnackBar)
  const { copyLink } = useCopyReportLink()
  const selectedIdfTeam = createAccountStore((s) => s.selectedIdfTeam)

  const [activities, setActivities] = useState<FilteredActivityItem[]>([])
  const [isLoadingActivities, setIsLoadingActivities] = useState(false)
  const [selectedActivityIds, setSelectedActivityIds] = useState<number[]>([])
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null)

  const openMenu = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation()
    setMenuAnchor(e.currentTarget)
  }
  const closeMenu = () => setMenuAnchor(null)

  const isDailyReport = preset.reportType === "daily"

  // 프리셋의 필터 조건으로 액티비티 목록 로드
  const loadActivities = useCallback(async () => {
    setIsLoadingActivities(true)
    try {
      const { dateStart, dateFinish } = resolvePeriodDates(
        preset.periodType,
        preset.customDateStart,
        preset.customDateEnd,
      )
      const start = startOfDay(parseISO(dateStart)).valueOf()
      const end = addDays(startOfDay(parseISO(dateFinish)), 1).valueOf()

      const result = await readFilteredActivities({
        start,
        end,
        type: preset.activityTypes.length > 0 ? preset.activityTypes.join(",") : undefined,
        idfPlayer:
          preset.selectedPlayerIds.length > 0 ? preset.selectedPlayerIds.join(",") : undefined,
        idfSeasonWeek: preset.seasonWeekIds.length > 0 ? preset.seasonWeekIds.join(",") : undefined,
        matchDay: preset.matchDayTypes.length > 0 ? preset.matchDayTypes.join(",") : undefined,
      })

      const list = ((result as any)?.response ?? []) as FilteredActivityItem[]
      setActivities(list)

      // 기본 선택: ANALYZED만 선택, daily=첫 번째, trend=전체 ANALYZED
      const analyzedIds = list.filter((a) => a.status === "ANALYZED").map((a) => a.idfActivity)

      if (isDailyReport) {
        setSelectedActivityIds(analyzedIds.length > 0 ? [analyzedIds[0]] : [])
      } else {
        setSelectedActivityIds(analyzedIds)
      }
    } catch {
      setActivities([])
    } finally {
      setIsLoadingActivities(false)
    }
  }, [preset, isDailyReport])

  useEffect(() => {
    loadActivities()
  }, [loadActivities])

  // 선택된 액티비티로 URL 파라미터 구성
  const buildParams = useCallback(() => {
    return presetToSearchParams(preset, selectedActivityIds)
  }, [preset, selectedActivityIds])

  // View Report
  const handleViewReport = useCallback(() => {
    if (selectedActivityIds.length === 0) {
      setSnackBar({
        open: true,
        message: t("noAnalyzedActivity", "No analyzed activities found for the selected filters."),
        severity: "warning",
      })

      return
    }

    const basePath = isDailyReport ? "/report/daily" : "/report/trend"
    const params = buildParams()
    router.push(`${basePath}?${params.toString()}`)
  }, [selectedActivityIds, isDailyReport, buildParams, router, setSnackBar, t])

  // Copy Link
  const handleCopyLink = useCallback(() => {
    if (selectedActivityIds.length === 0) {
      return
    }
    const params = buildParams()
    const url = generateReportUrl(preset.reportType, params, selectedIdfTeam)
    copyLink(url)
  }, [selectedActivityIds, buildParams, preset.reportType, copyLink, selectedIdfTeam])

  // PDF Export
  const handlePdfExport = useCallback(() => {
    onPdfExport(preset)
  }, [preset, onPdfExport])

  const isPdfDisabled = !preset.idfPdfTemplate || isPdfBusy || selectedActivityIds.length === 0

  // Activity selector value display
  const activityDisplayValue = useMemo(() => {
    if (selectedActivityIds.length === 0) {
      return t("selectActivity", "Select...")
    }
    if (isDailyReport) {
      const act = activities.find((a) => a.idfActivity === selectedActivityIds[0])

      return act ? act.name : ""
    }

    return `${selectedActivityIds.length} ${t("activities", "activities")}`
  }, [selectedActivityIds, activities, isDailyReport, t])

  return (
    <div className="QuickReportPresetRow">
      {/* 프리셋 이름 */}
      <div className="QuickReportPresetRow__name">
        <span
          className={`QuickReportPresetRow__type-badge QuickReportPresetRow__type-badge--${isDailyReport ? "daily" : "trend"}`}>
          {isDailyReport ? "A" : "T"}
        </span>
        <Tooltip title={preset.name} size="small">
          <span className="QuickReportPresetRow__name-text">{preset.name}</span>
        </Tooltip>
      </div>

      {/* Activity Selector */}
      <div className="QuickReportPresetRow__selector">
        {isDailyReport ? (
          <Selector
            type="select"
            value={selectedActivityIds[0] ?? ""}
            onChange={(e: any) => {
              const val = e.target.value as number
              setSelectedActivityIds(val ? [val] : [])
            }}
            displayEmpty
            fullWidth
            disabled={isLoadingActivities || activities.length === 0}
            renderValue={() => (
              <span className="QuickReportPresetRow__selector-value">
                {isLoadingActivities ? t("loading", "Loading...") : activityDisplayValue}
              </span>
            )}>
            {activities.map((a) => {
              const isDisabled = a.status !== "ANALYZED"
              const isSelected = selectedActivityIds[0] === a.idfActivity

              return (
                <MenuItem
                  key={a.idfActivity}
                  value={a.idfActivity}
                  disabled={isDisabled}
                  className="QuickReportPresetRow__option">
                  <span className="QuickReportPresetRow__option-label">{a.name}</span>
                  {isDisabled && (
                    <Chip type="default-small" label={t(REPORT_STATUS_OBJECT[a.status] ?? "")} />
                  )}
                  {!isDisabled && isSelected && (
                    <RiCheckLine size={16} className="QuickReportPresetRow__option-check" />
                  )}
                </MenuItem>
              )
            })}
          </Selector>
        ) : (
          <Selector
            type="select"
            multiple
            value={selectedActivityIds as any}
            onChange={(e: any) => {
              const ids = e.target.value as number[]
              // ANALYZED가 아닌 항목은 선택 불가이므로 필터링 보장
              const validIds = ids.filter((id) => {
                const act = activities.find((a) => a.idfActivity === id)

                return act?.status === "ANALYZED"
              })
              setSelectedActivityIds(validIds)
            }}
            displayEmpty
            fullWidth
            disabled={isLoadingActivities || activities.length === 0}
            renderValue={() => (
              <span className="QuickReportPresetRow__selector-value">
                {isLoadingActivities ? t("loading", "Loading...") : activityDisplayValue}
              </span>
            )}>
            {activities.map((a) => {
              const isDisabled = a.status !== "ANALYZED"
              const isChecked = selectedActivityIds.includes(a.idfActivity)

              return (
                <MenuItem
                  key={a.idfActivity}
                  value={a.idfActivity}
                  disabled={isDisabled}
                  className="QuickReportPresetRow__option">
                  <Checkbox checked={isChecked} disabled={isDisabled} />
                  <span className="QuickReportPresetRow__option-label">{a.name}</span>
                  {isDisabled && (
                    <Chip type="default-small" label={t(REPORT_STATUS_OBJECT[a.status] ?? "")} />
                  )}
                </MenuItem>
              )
            })}
          </Selector>
        )}
      </div>

      {/* 액션 — wide(≥620): 버튼 펼침 / narrow(<620): kebab 메뉴 (CSS로 토글) */}
      <div className="QuickReportPresetRow__actions-wide">
        <Tooltip title={t("refresh", "Refresh")} size="small">
          <span>
            <button
              className="QuickReportPresetRow__action-btn"
              onClick={loadActivities}
              disabled={isLoadingActivities}>
              <RiRefreshLine
                size={20}
                className={isLoadingActivities ? "QuickReportPresetRow__spin" : ""}
              />
            </button>
          </span>
        </Tooltip>
        <Tooltip title={t("button.viewReport", "View Report")} size="small">
          <span>
            <button
              className="QuickReportPresetRow__action-btn"
              onClick={handleViewReport}
              disabled={selectedActivityIds.length === 0}>
              <RiShareBoxFill size={20} />
            </button>
          </span>
        </Tooltip>
        <Tooltip title={t("quickExport", "PDF")} size="small">
          <span>
            <button
              className="QuickReportPresetRow__action-btn"
              onClick={handlePdfExport}
              disabled={isPdfDisabled}>
              <RiFileDownloadLine size={20} />
            </button>
          </span>
        </Tooltip>
        <Tooltip title={t("copyLink", "Copy Link")} size="small">
          <span>
            <button
              className="QuickReportPresetRow__action-btn"
              onClick={handleCopyLink}
              disabled={selectedActivityIds.length === 0}>
              <RiLinkM size={20} />
            </button>
          </span>
        </Tooltip>
      </div>

      {/* narrow(<620)에서만 노출 — View Report는 밖으로 빼서 직접 접근 */}
      <div className="QuickReportPresetRow__actions-view">
        <Tooltip title={t("button.viewReport", "View Report")} size="small">
          <span>
            <button
              className="QuickReportPresetRow__action-btn"
              onClick={handleViewReport}
              disabled={selectedActivityIds.length === 0}>
              <RiShareBoxFill size={20} />
            </button>
          </span>
        </Tooltip>
      </div>

      <div className="QuickReportPresetRow__actions-kebab">
        <IconButton type="ghost" icon="more-vertical" size="xs" onClick={openMenu} />
      </div>

      <Menu
        className="QuickReportRowMenu"
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}>
        <MenuItem
          disabled={isLoadingActivities}
          onClick={() => {
            loadActivities()
            closeMenu()
          }}>
          <RiRefreshLine size={16} />
          {t("refresh")}
        </MenuItem>
        <MenuItem
          disabled={isPdfDisabled}
          onClick={() => {
            handlePdfExport()
            closeMenu()
          }}>
          <RiFileDownloadLine size={16} />
          {t("quickExport", "Download PDF")}
        </MenuItem>
        <MenuItem
          disabled={selectedActivityIds.length === 0}
          onClick={() => {
            handleCopyLink()
            closeMenu()
          }}>
          <RiLinkM size={16} />
          {t("copyLink", "Copy Link")}
        </MenuItem>
      </Menu>
    </div>
  )
}
